
import { useState, useCallback } from 'react';
import { CVEItem } from '@/types/cve';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '../use-toast';

// All years available
export const YEARS_TO_LOAD = Array.from({ length: 24 }, (_, i) => 2025 - i);

export function useCVEYearData() {
  const [cveData, setCveData] = useState<Record<number, CVEItem[]>>({});
  const [loadingYears, setLoadingYears] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Current active year being viewed
  const [activeYear, setActiveYear] = useState(YEARS_TO_LOAD[0]); // Start with latest year (2025)
  
  // Load data for a specific year
  const loadYearData = useCallback(async (year: number) => {
    if (cveData[year] && cveData[year].length > 0) {
      return; // Skip if already loaded
    }

    setLoadingYears(prev => ({ ...prev, [year]: true }));
    
    try {
      const { data, error: downloadError } = await supabase
        .storage
        .from('dane')
        .download(`${year}.json`);
        
      if (downloadError) {
        console.error(`Error fetching ${year} data:`, downloadError);
        throw new Error(`Failed to fetch ${year} CVE data: ${downloadError.message}`);
      }
      
      if (!data) {
        console.error(`No ${year} data returned`);
        throw new Error(`No ${year} CVE data returned`);
      }
      
      // Parse the JSON data from the storage blob
      const text = await data.text();
      const yearData = JSON.parse(text);
      
      // Sort by published date (newest first)
      const sortedCVEs = yearData.sort((a: CVEItem, b: CVEItem) => 
        new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
      );
      
      setCveData(prev => ({...prev, [year]: sortedCVEs }));
    } catch (err) {
      console.error(`Error in ${year} CVE data fetch:`, err);
      toast({
        title: `Error loading ${year} CVE data`,
        description: `Could not load the ${year} CVE data. Please try again later.`,
        variant: "destructive",
      });
    } finally {
      setLoadingYears(prev => ({ ...prev, [year]: false }));
    }
  }, [cveData, toast]);

  // Change year
  const changeYear = (year: number) => {
    setActiveYear(year);
  };
  
  return {
    cveData,
    loadingYears,
    error,
    activeYear,
    loadYearData,
    changeYear
  };
}
